import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import clsx from 'clsx';
import { useTranslate } from 'core/hooks/useTranslate';
import { ROLES_GUID } from 'core/utils/user';
import React, { useEffect, useMemo, useState } from 'react';
import type { AssetImageId } from '../../../../atoms/AssetImage/AssetImage';
import { getDragHandleBindings, getInertProps, getLabelWithContext } from './rowA11y';

type UseModuleRowParams = {
  module: ModuleItemType;
  structureLevels: 1 | 2;
  isDragOverlay: boolean;
  isExpanded: boolean;
  activeDragType: 'module' | 'submodule' | null;
  activeParentModuleGuid: string | null;
  reorderMode: boolean;
  onToggleExpand?: () => void;
  onAddSubmodule?: (parentModuleGuid: string) => void;
  onOpenPhoto?: (item: ModuleItemType | SubmoduleItem) => void;
};

export function useModuleRow({
  module,
  structureLevels,
  isDragOverlay,
  isExpanded,
  activeDragType,
  activeParentModuleGuid,
  reorderMode,
  onToggleExpand,
  onAddSubmodule,
  onOpenPhoto,
}: UseModuleRowParams) {
  const { t } = useTranslate();

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: module.guid,
    disabled: isDragOverlay,
  });

  const [canAddImage, setCanAddImage] = useState(false);

  useEffect(() => {
    setCanAddImage(module.created_by?.role_guid === ROLES_GUID.TEACHER && !reorderMode);
  }, [module, reorderMode]);

  const { setNodeRef: setDroppableRef, isOver: isSubmoduleOver } = useDroppable({
    id: `submodule-zone-${module.guid}`,
    disabled: structureLevels !== 2,
  });

  const shouldShowSubmoduleDragOver = isSubmoduleOver && activeDragType === 'submodule' && activeParentModuleGuid !== module.guid;

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging || module.visibility === 0 ? 0.6 : 1,
    }),
    [transform, transition, isDragging, module.visibility]
  );

  const moduleName = module.lesson_name || module.section;

  const submoduleIds = useMemo(() => module.items?.map((item) => item.guid) ?? [], [module.items]);

  const hasSubmodules = structureLevels === 2 && (module.items?.length ?? 0) > 0;
  const canExpand = structureLevels === 2 && (reorderMode ? hasSubmodules : true);

  const isEditorialModule = module?.created_by?.role_guid === ROLES_GUID.EDITORIAL;
  const canOpenPhoto = !reorderMode && !isEditorialModule && Boolean(onOpenPhoto);

  const shouldRenderItemActions = !reorderMode;
  const shouldRenderCreatedByInfo = !isEditorialModule;
  const shouldRenderChevron = canExpand && !isDragOverlay;
  const shouldRenderSubmodules = structureLevels === 2;
  const shouldRenderAddSubmoduleButton = !isDragOverlay && Boolean(onAddSubmodule) && (isExpanded || !hasSubmodules) && !reorderMode;

  const rootRef = isDragOverlay ? undefined : setNodeRef;
  const rootStyle = isDragOverlay ? undefined : style;

  const {
    dragHandleProps,
    isDragHandleInteractive,
    rowDragListeners: mainDragListeners,
  } = getDragHandleBindings({ attributes, isDragOverlay, listeners, reorderMode });
  const dragHandleLabel = getLabelWithContext(t, t('common:DragToReorder'), moduleName);
  const chevronLabel = getLabelWithContext(t, t('organize-content:Toggle submodules'), moduleName);
  const photoLabel = getLabelWithContext(t, t('common:Add an image'), moduleName);
  const addSubmoduleLabel = t('organize-content:Add submodule');

  // Sin id en el overlay: mientras se arrastra, la fila original sigue montada y lo duplicaría.
  const submodulesId = isDragOverlay ? undefined : `module-row-submodules-${module.guid}`;
  const submodulesInertProps = getInertProps(!isExpanded);

  const rootClassName = clsx('module-row', {
    'module-row--dragging': isDragging,
    'module-row--overlay': isDragOverlay,
    'module-row--has-submodules': hasSubmodules,
    'module-row--expanded': isExpanded && hasSubmodules,
    'module-row--collapsed': !isExpanded && hasSubmodules,
    'module-row--hidden': module.visibility === 0,
  });

  const mainClassName = clsx('module-row__main', { 'module-row__main--edit-mode': !reorderMode });
  const dragHandleClassName = clsx('module-row__drag-handle', { 'module-row__drag-handle--hidden': !reorderMode });
  const imageClassName = clsx('module-row__image', { 'module-row__image--clickable': canOpenPhoto });
  const chevronClassName = clsx('module-row__chevron', { 'module-row__chevron--expanded': isExpanded });
  const submodulesClassName = clsx('module-row__submodules', {
    'module-row__submodules--expanded': isExpanded,
    'module-row__submodules--collapsed': !isExpanded,
    'module-row__submodules--dragging': isDragging,
    'module-row__submodules--drag-over': shouldShowSubmoduleDragOver,
  });

  const assetImageId: AssetImageId = canAddImage ? 'noModuleCoverAdd' : 'noModuleCover';

  function handleToggleExpand(e: React.MouseEvent) {
    e.stopPropagation();
    onToggleExpand?.();
  }

  function handleOpenPhoto(e: React.MouseEvent) {
    if (!canOpenPhoto) return;
    e.stopPropagation();
    onOpenPhoto?.(module);
  }

  function handleOpenPhotoKey(e: React.KeyboardEvent) {
    if (!canOpenPhoto) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    onOpenPhoto?.(module);
  }

  function handleAddSubmodule() {
    onAddSubmodule?.(module.guid);
  }

  const imageInteractiveProps = canOpenPhoto
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': photoLabel,
        onClick: handleOpenPhoto,
        onKeyDown: handleOpenPhotoKey,
      }
    : {};

  return {
    addSubmoduleLabel,
    rootRef,
    rootStyle,
    rootClassName,
    mainClassName,
    mainDragListeners,
    isDragHandleInteractive,
    dragHandleProps,
    setDragHandleRef: setActivatorNodeRef,
    dragHandleLabel,
    dragHandleClassName,
    imageClassName,
    imageInteractiveProps,
    assetImageId,
    moduleName,
    chevronClassName,
    chevronLabel,
    submodulesClassName,
    submodulesId,
    submodulesInertProps,
    setDroppableRef,
    submoduleIds,
    hasSubmodules,
    shouldRenderItemActions,
    shouldRenderCreatedByInfo,
    shouldRenderChevron,
    shouldRenderSubmodules,
    shouldRenderAddSubmoduleButton,
    handleToggleExpand,
    handleAddSubmodule,
  };
}
